import React from 'react';

import { useImageUpload } from '@hooks/useImageUpload';

import { Button } from '@components/Button';
import { ClockLoader } from '@components/ClockLoader';

import { ImageUploadProps } from './interfaces';

import { ImageContainer, ImagePreview, LoadingOverlay } from './styles';

/**
 * Image upload component
 */

export const ImageUpload: React.FC<ImageUploadProps> = ({
  onImageUpload,
  onError,
  className,
  successMessage,
  errorTitle,
  successTitle,
  altText = 'Uploaded image',
  buttonName,
  variant = 'primary',
  children,
}) => {
  const {
    imageUrl,
    loading,
    uploading,
    hasImage,
    fileInputRef,
    handleFileChange,
    triggerFileSelect,
    resetError,
  } = useImageUpload({
    onImageUpload,
    onError,
    successMessage,
    errorTitle,
    successTitle,
  });

  const handleButtonClick = () => {
    triggerFileSelect();
  };

  const handleImageLoadError = () => {
    resetError();
    onError?.('Image failed to load');
  };

  const renderContent = () => {
    // Show initial loading state
    if (loading && !hasImage) {
      return <ClockLoader />;
    }
    if (hasImage) {
      return (
        <>
          <ImagePreview src={imageUrl} alt={altText} onError={handleImageLoadError} />
          {uploading && (
            <LoadingOverlay>
              <ClockLoader />
            </LoadingOverlay>
          )}
        </>
      );
    }
    return null;
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      <ImageContainer $hasImage={hasImage} className={className}>
        {renderContent()}
      </ImageContainer>

      {children}

      <Button
        type="button"
        variant={variant}
        onClick={handleButtonClick}
        label={buttonName}
        disabled={uploading}
      />
    </>
  );
};
